/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { TrackedArray } from 'tracked-built-ins';
import EmberObject, { action, computed, get } from '@ember/object';
import { not, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Alphabet from 'linced-app/enums/alphabet';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import NotificationType from 'linced-app/enums/notification-type';
import Options from '@linc-technologies/linced-common/enums/options';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';

export class PrimaryLearner extends EmberObject {
  person = null;

  @alias('person.legalLastInitial')
  initial;

  @alias('person.firstName')
  firstName;

  @alias('person.legalLastName')
  lastName;

  siblings = null;
  options = null;
  selected = null;

  // Plain getter, not @alias: `selected` is TrackedArray-backed, and a
  // classic macro like @alias doesn't reliably observe a TrackedArray's
  // autotracked `.length` reads the way it does a real EmberArray's.
  get numSelected() {
    return this.selected.length;
  }
}

export default class StatementsController extends Controller {
  // Services
  @service
  options;

  @service
  print;

  @service
  session;

  @service
  store;

  @service
  strings;

  @alias('session.school.name')
  schoolName;

  @alias('session.school')
  school;

  // Default values used to pre-populate the email fields. Passed to
  // Finance::ProhibitedLanguageAlert as @initialText so that prohibited words
  // already present in the system default are not flagged (SMOS-012).
  @computed('schoolName')
  get defaultEmailSubject() {
    return `Financial Statement from ${this.schoolName}`;
  }

  defaultEmailMessage =
    'Recent updates have been applied to student billing accounts.  Click the link below to log in and view your statement.';

  // NSW DoE prohibited financial language (SMOS-012).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  emailSubjectViolations = [];

  @action
  setEmailSubjectViolations(value) {
    this.emailSubjectViolations = value;
  }

  @tracked
  emailMessageViolations = [];

  @action
  setEmailMessageViolations(value) {
    this.emailMessageViolations = value;
  }

  // hasEmailLanguageViolations gates the stepper "Next" button when the
  // email distribution method is selected and violations are present.
  @computed(
    'distributionMethod',
    'emailSubjectViolations.[]',
    'emailMessageViolations.[]',
  )
  get hasEmailLanguageViolations() {
    if (this.distributionMethod !== 'email') return false;
    return (
      !isEmpty(this.emailSubjectViolations) ||
      !isEmpty(this.emailMessageViolations)
    );
  }

  @optionAlias('finance:settings:caregiverAccessEnabled')
  caregiverAccessEnabled;
  @optionAlias(Options.NOTIFICATIONS_EMAIL_ENABLED)
  emailNotificationsEnabled;

  @not('emailNotificationsEnabled')
  emailNotificationsDisabled;

  alphabet = Alphabet.values;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @tracked
  generationInProgress = false;

  @tracked
  errorMessage;

  @tracked
  progressMessage = 'Gathering data';

  @tracked
  distributionMethod = '';

  @action
  setDistributionMethod(value) {
    this.distributionMethod = value;
  }

  @tracked
  emailSubject = '';

  @action
  setEmailSubject(value) {
    this.emailSubject = value;
  }

  @tracked
  emailMessage = '';

  @action
  setEmailMessage(value) {
    this.emailMessage = value;
  }

  @tracked
  excludePaidItems = false;

  @action
  setExcludePaidItems(value) {
    this.excludePaidItems = value;
  }

  @tracked
  excludeZeroBalance = false;

  @action
  setExcludeZeroBalance(value) {
    this.excludeZeroBalance = value;
  }

  @tracked
  includeHomeroom = false;

  @action
  setIncludeHomeroom(value) {
    this.includeHomeroom = value;
  }

  @tracked
  learnerName = 'none';

  @action
  setLearnerName(value) {
    this.learnerName = value;
  }

  @tracked
  includeGroups = [];

  @tracked
  excludeGroups = [];

  @tracked
  selectedYearLevels = [];

  @tracked
  selectedPositions = [];

  @tracked
  bulkSelectedLearners = [];

  @action
  setBulkSelectedLearners(value) {
    this.bulkSelectedLearners = value;
  }

  selectedLearners = [];

  @tracked
  numStatements = 0;

  @tracked
  numStatementsFailed = 0;

  getFamilyMapper() {
    let store = this.store;
    let bulkSelected = this.bulkSelectedLearners;
    let method = this.distributionMethod;

    return (person) => {
      let result = PrimaryLearner.create({
        person,
        // siblings is built once, synchronously, right here - a plain array
        // is fine. options/selected are genuinely different: options gets
        // pushed asynchronously (as caregiver promises resolve, well after
        // this family is already rendered), the template iterates options
        // directly with {{#each}}, and numSelected needs selected's own
        // length changes (from toggleOption) to be observable - so both
        // are TrackedArray, not plain arrays.
        siblings: [],
        options: new TrackedArray(),
        selected: new TrackedArray(),
      });

      // Push the learner into the siblings array, so that we can just iterate over it to get all of them at once.
      result.siblings.push(person);

      // If we have any siblings to iterate over, add any non-former students to the siblings array.
      // We're effectively reimplementing the sibling computed properties from the learner subrecord,
      // because they haven't necessarily computed yet and so might not return anything useful here.
      if (person.get('learner.siblings.length') > 0) {
        person.get('learner.siblings').forEach((sibling) => {
          if (sibling.schoolId !== this.get('session.activeSchool')) {
            // Not currently associated with this school; skip.
            return;
          }
          // Model contains all current, pending and pre-enrolled learners.  If we don't have 'em, they've left.
          let siblingPerson = store.peekRecord('person', sibling.personId);
          if (
            !isEmpty(siblingPerson) &&
            siblingPerson.get('enrolmentStatus') !== EnrolmentStatus.FORMER &&
            siblingPerson.get('enrolmentStatus') !== EnrolmentStatus.ARCHIVED
          ) {
            result.siblings.push(siblingPerson);
          }
        });
      }

      // Set up available options for this family.
      if (method === 'print') {
        result.options.push({
          label: 'Generate statement',
          value: 'statement',
        });
      } else {
        let contacts = result.person.get('learner.primaryContacts');
        if (!isEmpty(contacts)) {
          contacts.forEach((contact) => {
            if (!isEmpty(contact.caregiverId) && contact.billPayer) {
              // The route loads the caregivers.
              let caregiver = contact.caregiver;
              if (caregiver) {
                result.options.push({
                  label: caregiver.fullName,
                  value: contact.caregiverId,
                  disabled: isEmpty(contact.emailAddress),
                });
              }
            }
          });
        }
      }

      // If any of the learners in this family have been bulk selected, mark selection as appropriate.
      if (
        result.siblings.some((person) =>
          bulkSelected.includes(person.get('id')),
        )
      ) {
        if (method === 'print') {
          result.selected.push('statement');
        } else {
          // TODO: When multiple/split accounts are implemented, this will need some thought.
          let contacts = result.person.get('learner.primaryContacts');
          if (!isEmpty(contacts)) {
            contacts.forEach((contact) => {
              // Skip pending contacts, since we don't have a caregiverId and thus can't notify them.
              if (!isEmpty(contact.caregiverId) && contact.billPayer) {
                if (!isEmpty(contact.emailAddress)) {
                  // We have an email address for this contact; select them.
                  result.selected.push(contact.caregiverId);
                }
              }
            });
          }
        }
      }

      return result;
    };
  }

  @computed('model.learners.[]')
  get eldest() {
    return this.model.learners.filter(
      (item) => get(item, 'learner.siblingPosition') === 1,
    );
  }

  @computed('model.learners.[]')
  get youngest() {
    return this.model.learners.filter((person) => {
      return (
        person.get('learner.siblingPosition') ===
        person.get('learner.siblingCount')
      );
    });
  }

  @tracked primaryLearners = [];

  generateInitialSelection() {
    let learners;
    if (
      this.distributionMethod === 'print' &&
      this.learnerName === 'youngest'
    ) {
      learners = this.youngest;
    } else {
      learners = this.eldest;
    }

    let mapper = this.getFamilyMapper();

    this.primaryLearners = learners.map(mapper);
  }

  // Plain getters, not @mapBy/@sum: numSelected (per-family) is itself a
  // plain getter over TrackedArray-backed state, and classic macros don't
  // reliably observe a plain native getter's autotracked reads.
  get numSelectedPerLearner() {
    return this.primaryLearners.map((learner) => learner.numSelected);
  }

  get numSelected() {
    return this.numSelectedPerLearner.reduce((total, n) => total + n, 0);
  }

  generatePrintStatements() {
    const statementBatchSize = 50;
    let selected = this.primaryLearners.filter((item) => item.selected.length);
    let thisYear = moment().year();
    let statementArray = [];
    let statementPromises = selected.reduce((chain, learner) => {
      let personIds = learner.siblings.map((item) => item.id);
      return chain.then(() => {
        return hash({
          items: this.store.query('finance/line-item', {
            learnerIds: JSON.stringify(personIds),
            include: 'transactions',
          }),
          accounts: this.store.query('finance/account', {
            personIds: JSON.stringify(personIds),
          }),
        }).then(async (data) => {
          data.items = data.items.filter((item) => {
            let itemYear = moment(item.date).year();
            // Skip paid items (if in a previous year, or if option to skip is selected).
            return !(
              !item.balance &&
              (this.excludePaidItems || itemYear < thisYear)
            );
          });

          // NOTE: The following uses `!account.balance` as it can come through as null.
          let hasNoPIA = data.accounts.every((account) => !account.balance);
          if (data.items.length === 0 && hasNoPIA) {
            // No line items or PIA balances to show.  Don't bother to generate a statement.
            return;
          }

          if (
            this.excludeZeroBalance &&
            data.items.every((item) => !item.balance) &&
            hasNoPIA
          ) {
            // Don't generate statements with $0 balances (if requested).
            return;
          }

          let options = {
            includeHomeroomName: this.includeHomeroom,
            includeLearnerName: this.learnerName !== 'none',
          };
          let statementData = await this.print._generateStatementData(
            data.items,
            learner.person,
            data.accounts,
            'statement.docx',
            options,
          );
          statementArray.push(statementData);
        });
      });
    }, Promise.resolve());

    this.numStatements = 0;
    statementPromises.then(() => {
      this.progressMessage = 'Generating statements';
      let slices = [];
      let noOfDocuments = Math.ceil(statementArray.length / statementBatchSize);
      for (let i = 0; i < noOfDocuments; i++) {
        slices[i] = statementArray.splice(0, statementBatchSize);
      }
      let printPromises = slices.reduce((chain, slice, index) => {
        return chain.then(() => {
          return new Promise((innerResolve) => {
            slice = compact(slice);
            let data = slice.reduce((data, statement, index) => {
              data[index] = statement;
              return data;
            }, []);

            let onComplete = (succeeded) => {
              if (succeeded) {
                this.incrementProperty('numStatements', slice.length);
                this.progressMessage = `Generated ${this.numStatements} statements.  Processing next batch`;
              } else {
                // Track failures, so we can tell the user.
                this.incrementProperty('numStatementsFailed', slice.length);
              }
              innerResolve();
            };
            this.print.download(
              'ziptemplate',
              { data },
              `statements_${index + 1}.docx`,
              onComplete,
            );
          });
        });
      }, Promise.resolve());

      printPromises
        .catch(() => {
          this.errorMessage =
            'Something went wrong.  Please try again later, or contact Hero support if the problem persists.';
        })
        .finally(() => {
          this.generationInProgress = false;
        });
    });
  }

  generateEmailStatements() {
    const statementBatchSize = 100;
    let selected = this.primaryLearners.filter((item) => item.selected.length);
    let statementArray = [];
    let statementPromises = selected.reduce((chain, learner) => {
      let personIds = learner.siblings.map((item) => item.id);
      return chain.then(() => {
        // Retrieve line items and accounts, so that we can determine whether or not to send the email
        // based on whether they have anything to see.
        return hash({
          items: this.store.query('finance/line-item', {
            learnerIds: JSON.stringify(personIds),
            include: 'transactions',
          }),
          accounts: this.store.query('finance/account', {
            personIds: JSON.stringify(personIds),
          }),
        }).then((data) => {
          // NOTE: The following uses `!account.balance` as it can come through as null.
          let hasNoPIA = data.accounts.every((account) => !account.balance);
          if (data.items.length === 0 && hasNoPIA) {
            // No line items or PIA balances to show.  Don't bother to generate an email.
            return;
          }

          if (data.items.every((item) => !item.balance) && hasNoPIA) {
            // Don't email those with $0 balances.
            return;
          }

          let primaryAccount = data.accounts.find(
            (item) => item.person_id === learner.person.id,
          );
          if (isEmpty(primaryAccount)) {
            // Something's gone wrong here - we don't have a Finance Account for the primary learner!
            return;
          }

          statementArray.push(
            learner.selected.map((personId) => {
              let caregiver = findBy(
                learner.person.get('learner.primaryContacts'),
                'caregiverId',
                personId,
              );
              let salutation = '';
              if (!isEmpty(caregiver)) {
                // If we found the caregiver (should always happen), use their name for the salutation.
                salutation = caregiver.get('fullName');
              }
              if (isEmpty(salutation)) {
                // If we don't have a salutation yet, use the account salutation.
                salutation = this.print.getAccountSalutation(
                  primaryAccount,
                  data.accounts,
                );
              }

              return {
                personId,
                salutation,
              };
            }),
          );
        });
      });
    }, Promise.resolve());

    statementPromises.then(() => {
      this.progressMessage = 'Generating statements';
      let statementData = compact(statementArray);

      if (statementData.length === 0) {
        // No statements - don't actually try to send notifications.
        this.numStatements = 0;
        this.generationInProgress = false;
        return;
      }

      let allRecipients = statementData.reduce((recipients, data) => {
        if (isEmpty(data)) {
          return recipients;
        }
        // pushObjects returns the array itself (needed as the reduce
        // accumulator); native push returns the new length instead, so the
        // push and the return have to be separate statements here.
        recipients.push(...data);
        return recipients;
      }, []);

      let recipientSlices = [];
      let noOfNotifications = Math.ceil(
        allRecipients.length / statementBatchSize,
      );
      for (let i = 0; i < noOfNotifications; i++) {
        recipientSlices[i] = allRecipients.splice(0, statementBatchSize);
      }
      let notificationPromises = recipientSlices.reduce((chain, recipients) => {
        return chain.then(() => {
          return this.store
            .createRecord('notifications/notification', {
              schoolId: this.session.school,
              notificationType: NotificationType.BILLING_STATEMENT,
              params: {
                subject: this.emailSubject,
                message: this.emailMessage,
                recipients,
              },
            })
            .save()
            .then(() => {
              this.incrementProperty('numStatements', recipients.length);
              this.progressMessage = `Generated ${this.numStatements} statements.  Processing next batch`;
            })
            .catch(() => {
              this.incrementProperty('numStatementsFailed', recipients.length);
            });
        });
      }, Promise.resolve());

      notificationPromises
        .catch(() => {
          this.errorMessage =
            'Something went wrong.  Please try again later, or contact Hero support if the problem persists.';
        })
        .finally(() => {
          this.generationInProgress = false;
        });
    });
  }

  init() {
    super.init(...arguments);
    this.reset();
  }

  @action
  toggleGroup(listType, group) {
    if (listType !== 'include' && listType !== 'exclude') {
      return;
    }
    // NOTE: this used to mix `.push()` (native, mutating the tracked field
    // in place - never reactive, @tracked only fires on reassignment) with
    // `.removeObject()` (Ember-only, but did fire via genuine array-content
    // notification since this field was A()-backed) - so adding a group to
    // the list silently never updated the UI, while removing one did.
    // Rebuild-and-reassign fixes both directions the same way.
    let key = `${listType}Groups`;
    let selected = this[key];
    if (selected.find((item) => item.id === group.id)) {
      this[key] = selected.filter((item) => item.id !== group.id);
    } else {
      this[key] = [...selected, group];
    }
  }

  @action
  toggleYearLevel(yearLevel) {
    if (this.selectedYearLevels.includes(yearLevel)) {
      this.selectedYearLevels = this.selectedYearLevels.filter(
        (item) => item !== yearLevel,
      );
    } else {
      this.selectedYearLevels = [...this.selectedYearLevels, yearLevel];
    }
  }

  @action
  toggleByPosition(position) {
    if (this.selectedPositions.includes(position)) {
      this.selectedPositions = this.selectedPositions.filter(
        (item) => item !== position,
      );
    } else {
      this.selectedPositions = [...this.selectedPositions, position];
    }
  }

  @action
  applyBulkSelection() {
    this.generateInitialSelection();
  }

  @action
  toggleOption(family, option) {
    let pos = family.selected.indexOf(option);
    if (pos > -1) {
      family.selected.splice(pos, 1);
    } else {
      family.selected.push(option);
    }
  }

  @action
  generateStatements() {
    this.generationInProgress = true;
    switch (this.distributionMethod) {
      case 'print':
        this.generatePrintStatements();
        break;

      case 'email':
        this.generateEmailStatements();
        break;
    }
  }

  @action
  reset() {
    this.currentStep = 0;
    this.generationInProgress = false;
    this.progressMessage = 'Gathering data';
    this.distributionMethod = '';
    this.emailSubject = this.defaultEmailSubject;
    this.emailMessage = this.defaultEmailMessage;
    this.excludePaidItems = false;
    this.excludeZeroBalance = false;
    this.includeHomeroom = false;
    this.learnerName = 'none';
    this.includeGroups = [];
    this.excludeGroups = [];
    this.selectedYearLevels = [];
    this.selectedPositions = [];
    this.bulkSelectedLearners = [];
    this.primaryLearners = [];
    this.numStatements = 0;
    this.numStatementsFailed = 0;
  }
}
